import { isUrlInDomainList } from '@/hooks/useRequiredInterceptor';
import { syncGntcConfig } from '@/utils/syncGntcConfig';
import axios from 'axios';
import React from 'react';
import { ContactInfo } from './Contact.types';
import { resolveContactInfo } from './useContactManager';
import { useUserRedirection } from '@/context/Contact/useUserRedirection';

/**
 * Hook that intercepts axios requests to handle user portal access based on contact status
 *
 * @param contactInfo - Current contact information for the user
 * @param trustedDomains - List of domains that should be intercepted
 * @returns void
 */
export const useContactStatusInterceptor = ({
  contactInfo,
  trustedDomains,
}: {
  contactInfo: ContactInfo | null;
  trustedDomains: string[];
}): void => {
  const { handleRegistrationDirection } = useUserRedirection();

  // Store contact info in ref to avoid stale closures in interceptor
  const contactInfoRef = React.useRef<ContactInfo | null>(null);

  // Keep ref in sync with latest contact info
  React.useEffect(() => {
    contactInfoRef.current = contactInfo;
  }, [contactInfo]);

  // Setup request interceptor
  React.useEffect(() => {
    // Add interceptor to handle portal access based on contact status
    const ejectId = axios.interceptors.request.use(async (config) => {
      // Skip interceptor for non-trusted domains
      if (!isUrlInDomainList(config.url, trustedDomains)) {
        return config;
      }

      const resolvedContactInfo = resolveContactInfo(contactInfoRef.current || {});
      const { portalRegistrationStatus, betaParticipantStatus } = resolvedContactInfo;

      await handleRegistrationDirection({
        portalRegistrationStatus,
        betaParticipantStatus,
      });

      return config;
    });

    // Sync config on mount
    syncGntcConfig();

    // Cleanup interceptor and sync config on unmount
    return () => {
      axios.interceptors.request.eject(ejectId);
      syncGntcConfig();
    };
  }, [handleRegistrationDirection, trustedDomains]);
};
